import { scan, attributes, createOptions } from '@emmetio/html-matcher';
import { scan as scan$1, splitValue } from '@emmetio/css-matcher';
import expand, { stylesheetAbbreviation, markupAbbreviation } from 'emmet';

const pairs = {
    '{': '}',
    '[': ']',
    '(': ')'
};
const pairsEnd = [];
for (const key of Object.keys(pairs)) {
    pairsEnd.push(pairs[key]);
}
/**
 * Returns `true` if given character code is a space
 */
function isSpace(code) {
    return code === 32 /* space */
        || code === 9 /* tab */
        || code === 160 /* non-breaking space */
        || code === 10 /* LF */
        || code === 13; /* CR */
}
function pushRange(ranges, range) {
    const prev = ranges[ranges.length - 1];
    if (range && range[0] !== range[1] && (!prev || prev[0] !== range[0] || prev[1] !== range[1])) {
        ranges.push(range);
    }
}
/**
 * Returns ranges of tokens in given value. Tokens are space-separated words.
 */
function tokenList(value, offset = 0) {
    const ranges = [];
    const len = value.length;
    let pos = 0;
    let start = 0;
    let end = 0;
    while (pos < len) {
        end = pos;
        const ch = value.charCodeAt(pos++);
        if (isSpace(ch)) {
            if (start !== end) {
                ranges.push([offset + start, offset + end]);
            }
            while (isSpace(value.charCodeAt(pos))) {
                pos++;
            }
            start = pos;
        }
    }
    if (start !== pos) {
        ranges.push([offset + start, offset + pos]);
    }
    return ranges;
}
/**
 * Check if given character is a quote
 */
function isQuote(ch) {
    return ch === '"' || ch === '\'';
}
/**
 * Returns value of given attribute, parsed by Emmet HTML matcher
 */
function attributeValue(attr) {
    const { value } = attr;
    return value && isQuoted(value)
        ? value.slice(1, -1)
        : value;
}
function attributeValueRange(tag, attr, offset = 0) {
    let valueStart = attr.valueStart;
    let valueEnd = attr.valueEnd;
    if (isQuote(tag[valueStart])) {
        valueStart++;
    }
    if (isQuote(tag[valueEnd - 1]) && valueEnd > valueStart) {
        valueEnd--;
    }
    return [offset + valueStart, offset + valueEnd];
}
/**
 * Check if given value is either quoted or written as expression
 */
function isQuoted(value) {
    return !!value && (isQuotedString(value) || isExprString(value));
}
/**
 * Check if given string is quoted with single or double quotes
 */
function isQuotedString(str) {
    return str.length > 1 && isQuote(str[0]) && str[0] === str.slice(-1);
}
/**
 * Check if given string contains expression, e.g. wrapped with `{` and `}`
 */
function isExprString(str) {
    return str[0] === '{' && str.slice(-1) === '}';
}
/**
 * Returns last element of given array
 */
function last(arr) {
    return arr.length > 0 ? arr[arr.length - 1] : undefined;
}

/**
 * Check if there’s open or self-closing tag under given `pos` location in source code.
 * If found, returns its name, range in source and parsed attributes
 */
function getOpenTag(code, pos) {
    let tag = void 0;
    // Find open or self-closing tag, closest to given position
    scan(code, (name, type, start, end) => {
        if (start < pos && end > pos) {
            tag = { name, type, start, end };
            if (type === 1 /* Open */ || type === 3 /* SelfClose */) {
                tag.attributes = shiftAttributeRanges(attributes(code.slice(start, end), name), start);
            }
            return false;
        }
        if (end > pos) {
            return false;
        }
    });
    return tag;
}
/**
 * Returns list of matched tags in given source code
 */
function getTagMatches(code, options) {
    const opt = createOptions(options);
    const stack = [];
    const result = [];
    scan(code, (name, type, start, end) => {
        if (type === 3 /* SelfClose */) {
            result.push({ name, open: [start, end] });
        }
        else if (type === 1 /* Open */) {
            const item = { name, open: [start, end] };
            stack.push(item);
            result.push(item);
        }
        else {
            // Handle closing tag
            while (stack.length) {
                const item = stack.pop();
                if (item.name === name) {
                    item.close = [start, end];
                    break;
                }
            }
        }
    }, opt);
    return result;
}
/**
 * Finds tag match for given position
 */
function findTagMatch(source, pos, options) {
    if (typeof source === 'string') {
        source = getTagMatches(source, options);
    }
    let candidate;
    source.some(match => {
        const start = match.open[0];
        const end = match.close ? match.close[1] : match.open[1];
        if (pos < start) {
            // All the following tags will be after given position, stop searching
            return true;
        }
        if (pos > start && pos < end) {
            candidate = match;
        }
    });
    return candidate;
}
/**
 * Returns list of ranges for Select Next/Previous Item action
 */
function selectItemHTML(code, pos, isPrev) {
    return isPrev ? selectPreviousItem(code, pos) : selectNextItem(code, pos);
}
/**
 * Returns list of ranges for Select Next Item action
 */
function selectNextItem(code, pos) {
    let result = void 0;
    // Find open or self-closing tag, closest to given position
    scan(code, (name, type, start, end) => {
        if ((type === 1 /* Open */ || type === 3 /* SelfClose */) && end > pos) {
            // Found open or self-closing tag
            result = getTagSelectionModel(code, name, start, end);
            return false;
        }
    });
    return result;
}
/**
 * Returns list of ranges for Select Previous Item action
 */
function selectPreviousItem(code, pos) {
    let lastType = null;
    let lastName = '';
    let lastStart = -1;
    let lastEnd = -1;
    // We should find the closest open or self-closing tag left to given `pos`.
    scan(code, (name, type, start, end) => {
        if (start >= pos) {
            return false;
        }
        if (type === 1 /* Open */ || type === 3 /* SelfClose */) {
            // Found open or self-closing tag
            lastName = name;
            lastType = type;
            lastStart = start;
            lastEnd = end;
        }
    });
    if (lastType !== null) {
        return getTagSelectionModel(code, lastName, lastStart, lastEnd);
    }
}
/**
 * Parses open or self-closing tag in `start:end` range of `code` and returns its
 * model for selecting items
 * @param code Document source code
 * @param name Name of matched tag
 */
function getTagSelectionModel(code, name, start, end) {
    const ranges = [
        // Add tag name range
        [start + 1, start + 1 + name.length]
    ];
    // Parse and add attributes ranges
    const tagSrc = code.slice(start, end);
    for (const attr of attributes(tagSrc, name)) {
        if (attr.value != null) {
            // Attribute with value
            pushRange(ranges, [start + attr.nameStart, start + attr.valueEnd]);
            // Add (unquoted) value range
            const val = valueRange(attr);
            if (val[0] !== val[1]) {
                pushRange(ranges, [start + val[0], start + val[1]]);
                if (attr.name === 'class') {
                    // For class names, split value into space-separated tokens
                    const tokens = tokenList(tagSrc.slice(val[0], val[1]), start + val[0]);
                    for (const token of tokens) {
                        pushRange(ranges, token);
                    }
                }
            }
        }
        else {
            // Attribute without value (boolean)
            pushRange(ranges, [start + attr.nameStart, start + attr.nameEnd]);
        }
    }
    return { start, end, ranges };
}
/**
 * Returns value range of given attribute. Value range is unquoted.
 */
function valueRange(attr) {
    const value = attr.value;
    const ch = value[0];
    const lastCh = value[value.length - 1];
    if (ch === '"' || ch === '\'') {
        return [
            attr.valueStart + 1,
            attr.valueEnd - (lastCh === ch ? 1 : 0)
        ];
    }
    if (ch === '{' && lastCh === '}') {
        return [
            attr.valueStart + 1,
            attr.valueEnd - 1
        ];
    }
    return [attr.valueStart, attr.valueEnd];
}
function shiftAttributeRanges(attrs, offset) {
    attrs.forEach(attr => {
        attr.nameStart += offset;
        attr.nameEnd += offset;
        if ('value' in attr) {
            attr.valueStart += offset;
            attr.valueEnd += offset;
        }
    });
    return attrs;
}

/**
 * Returns context CSS section for given location in source code
 * @param properties Parse inner properties
 */
function getCSSSection(code, pos, properties) {
    const stack = [];
    const pool = [];
    let result;
    scan$1(code, (type, start, end, delimiter) => {
        if (start > pos && !stack.length) {
            return false;
        }
        if (type === "selector" /* Selector */) {
            stack.push(allocRange(pool, start, end, delimiter));
        }
        else if (type === "blockEnd" /* BlockEnd */) {
            const sel = stack.pop();
            if (sel && sel[0] <= pos && pos <= end) {
                result = {
                    start: sel[0],
                    end,
                    bodyStart: sel[2] + 1,
                    bodyEnd: start
                };
                return false;
            }
            releaseRange(pool, sel);
        }
    });
    if (result && properties) {
        result.properties = parseProperties(code, result.bodyStart, result.bodyEnd);
    }
    return result;
}
/**
 * Returns list of ranges for Select Next/Previous CSS Item  action
 */
function selectItemCSS(code, pos, isPrev) {
    return isPrev ? selectPreviousItem$1(code, pos) : selectNextItem$1(code, pos);
}
/**
 * Returns regions for selecting next item in CSS
 */
function selectNextItem$1(code, pos) {
    let result = void 0;
    let pendingProperty = void 0;
    scan$1(code, (type, start, end, delimiter) => {
        if (start < pos) {
            return;
        }
        if (type === "selector" /* Selector */) {
            result = { start, end, ranges: [[start, end]] };
            return false;
        }
        else if (type === "propertyName" /* PropertyName */) {
            pendingProperty = [start, end, delimiter];
        }
        else if (type === "propertyValue" /* PropertyValue */) {
            result = {
                start,
                end: delimiter !== -1 ? delimiter + 1 : end,
                ranges: []
            };
            if (pendingProperty) {
                // Full property range
                result.start = pendingProperty[0];
                pushRange(result.ranges, [pendingProperty[0], result.end]);
            }
            // Full value range
            pushRange(result.ranges, [start, end]);
            // Value fragments
            for (const r of splitValue(code.substring(start, end))) {
                pushRange(result.ranges, [r[0] + start, r[1] + start]);
            }
            return false;
        }
        else if (pendingProperty) {
            result = {
                start: pendingProperty[0],
                end: pendingProperty[1],
                ranges: [[pendingProperty[0], pendingProperty[1]]]
            };
            return false;
        }
    });
    return result;
}
/**
 * Returns regions for selecting previous item in CSS
 */
function selectPreviousItem$1(code, pos) {
    const state = {
        type: null,
        start: -1,
        end: -1,
        valueStart: -1,
        valueEnd: -1,
        valueDelimiter: -1,
    };
    scan$1(code, (type, start, end, delimiter) => {
        // Accumulate context until we reach given position
        if (start >= pos && type !== "propertyValue" /* PropertyValue */) {
            return false;
        }
        if (type === "selector" /* Selector */ || type === "propertyName" /* PropertyName */) {
            state.start = start;
            state.end = end;
            state.type = type;
            state.valueStart = state.valueEnd = state.valueDelimiter = -1;
        }
        else if (type === "propertyValue" /* PropertyValue */) {
            state.valueStart = start;
            state.valueEnd = end;
            state.valueDelimiter = delimiter;
        }
    });
    if (state.type === "selector" /* Selector */) {
        return {
            start: state.start,
            end: state.end,
            ranges: [[state.start, state.end]]
        };
    }
    if (state.type === "propertyName" /* PropertyName */) {
        const result = {
            start: state.start,
            end: state.end,
            ranges: []
        };
        if (state.valueStart !== -1) {
            result.end = state.valueDelimiter !== -1 ? state.valueDelimiter + 1 : state.valueEnd;
            // Full property range
            pushRange(result.ranges, [state.start, result.end]);
            // Full value range
            pushRange(result.ranges, [state.valueStart, state.valueEnd]);
            // Value fragments
            for (const r of splitValue(code.substring(state.valueStart, state.valueEnd))) {
                pushRange(result.ranges, [r[0] + state.valueStart, r[1] + state.valueStart]);
            }
        }
        else {
            pushRange(result.ranges, [state.start, state.end]);
        }
        return result;
    }
}
/**
 * Parses properties in `from:to` fragment of `code`. Note that `from:to` must
 * point to CSS section content, e.g. *inside* `{` and `}` (or top-level code context),
 * all properties found in nested sections will be ignored
 */
function parseProperties(code, from = 0, to = code.length) {
    const fragment = code.substring(from, to);
    const result = [];
    const pool = [];
    let pendingName;
    let nested = 0;
    let before = from;
    scan$1(fragment, (type, start, end, delimiter) => {
        if (type === "selector" /* Selector */) {
            nested++;
        }
        else if (type === "blockEnd" /* BlockEnd */) {
            nested--;
            before = from + end;
        }
        else if (!nested) {
            if (type === "propertyName" /* PropertyName */) {
                if (pendingName) {
                    // Create property with empty value
                    const valuePos = pendingName[2];
                    result.push(createProperty(fragment, pendingName, before, valuePos, valuePos, valuePos, from));
                    releaseRange(pool, pendingName);
                    before = from + start;
                }
                pendingName = allocRange(pool, start, end, delimiter);
            }
            else if (type === "propertyValue" /* PropertyValue */) {
                if (pendingName) {
                    result.push(createProperty(fragment, pendingName, before, start, end, delimiter, from));
                    releaseRange(pool, pendingName);
                    pendingName = void 0;
                }
                before = from + delimiter + 1;
            }
        }
    });
    return result;
}
function createProperty(code, name, before, start, end, delimiter, offset = 0) {
    return {
        name: [offset + name[0], offset + name[1]],
        value: [offset + start, offset + end],
        valueTokens: splitValue(code.substring(start, end), offset + start),
        before,
        after: offset + delimiter + 1,
    };
}
/**
 * Allocates new token range from pool
 */
function allocRange(pool, start, end, delimiter) {
    if (pool.length) {
        const range = pool.pop();
        range[0] = start;
        range[1] = end;
        range[2] = delimiter;
        return range;
    }
    return [start, end, delimiter];
}
/**
 * Releases given token range and pushes it back into the pool
 */
function releaseRange(pool, range) {
    range && pool.push(range);
    return null;
}

/**
 * Returns HTML context for given location in source code
 */
function getHTMLContext(code, pos, opt = {}) {
    const result = {
        type: 'html',
        ancestors: [],
        current: null,
        css: null
    };
    // Since we expect large input document, we’ll use pooling technique
    // for storing tag data to reduce memory pressure and improve performance
    const pool = [];
    const stack = [];
    const options = createOptions({ xml: opt.xml, allTokens: true });
    scan(code, (name, type, start, end) => {
        if (start >= pos) {
            // Moved beyond location, stop parsing
            return false;
        }
        if (start < pos && pos < end) {
            // Direct hit on element
            result.current = { name, type, range: [start, end] };
            return false;
        }
        if (type === 1 /* Open */ && isSelfClose(name, options)) {
            // Found empty element in HTML mode, mark is as self-closing
            type = 3 /* SelfClose */;
        }
        if (type === 1 /* Open */) {
            // Allocate tag object from pool
            stack.push(allocItem(pool, name, type, start, end));
        }
        else if (type === 2 /* Close */ && stack.length && last(stack).name === name) {
            // Release tag object for further re-use
            releaseItem(pool, stack.pop());
        }
    }, options);
    // Record stack elements as ancestors
    stack.forEach(item => {
        result.ancestors.push({
            name: item.name,
            range: [item.start, item.end]
        });
    });
    if (!opt.skipCSS) {
        // Detect if position is inside CSS context
        result.css = detectCSSContextFromHTML(code, pos, result);
    }
    return result;
}
/**
 * Returns CSS context for given location in source code
 */
function getCSSContext(code, pos, embedded) {
    const result = {
        type: 'css',
        ancestors: [],
        current: null,
        inline: false,
        embedded
    };
    const pool = [];
    const stack = [];
    scan$1(code, (type, start, end) => {
        if (start >= pos) {
            // Token behind specified location, stop parsing
            return false;
        }
        if (start < pos && pos <= end) {
            // Direct hit on token
            result.current = {
                name: code.slice(start, end),
                type,
                range: [start, end]
            };
            return false;
        }
        switch (type) {
            case "selector" /* Selector */:
            case "propertyName" /* PropertyName */:
                stack.push(allocItem(pool, code.slice(start, end), type, start, end));
                break;
            case "propertyValue" /* PropertyValue */:
            case "blockEnd" /* BlockEnd */:
                releaseItem(pool, stack.pop());
                break;
        }
    });
    stack.forEach(item => {
        result.ancestors.push({
            name: item.name,
            type: item.type,
            range: [item.start, item.end]
        });
    });
    return result;
}
/**
 * Returns embedded stylesheet syntax from given HTML context
 */
function getEmbeddedStyleSyntax(code, ctx) {
    const parent = last(ctx.ancestors);
    if (parent && parent.name === 'style') {
        for (const attr of attributes(code.slice(parent.range[0], parent.range[1]), parent.name)) {
            if (attr.name === 'type') {
                return attributeValue(attr);
            }
        }
    }
}
/**
 * Returns context for Emmet abbreviation from given HTML context
 */
function getMarkupAbbreviationContext(code, ctx) {
    const parent = last(ctx.ancestors);
    if (parent) {
        const attrs = {};
        for (const attr of attributes(code.slice(parent.range[0], parent.range[1]), parent.name)) {
            attrs[attr.name] = attributeValue(attr) || '';
        }
        return {
            name: parent.name,
            attributes: attrs
        };
    }
}
/**
 * Returns context for Emmet abbreviation from given CSS context
 */
function getStylesheetAbbreviationContext(ctx) {
    if (ctx.inline) {
        return { name: "@@property" /* Property */ };
    }
    const parent = last(ctx.ancestors);
    let scope = "@@global" /* Global */;
    if (ctx.current) {
        if (ctx.current.type === "propertyValue" /* PropertyValue */ && parent) {
            scope = parent.name;
        }
        else if ((ctx.current.type === "selector" /* Selector */ || ctx.current.type === "propertyName" /* PropertyName */) && !parent) {
            scope = "@@section" /* Section */;
        }
    }
    return {
        name: scope
    };
}
/**
 * Tries to detect CSS context from given HTML context and returns it
 */
function detectCSSContextFromHTML(code, pos, ctx) {
    let cssCtx = null;
    if (ctx.current) {
        // Maybe inline CSS?
        const elem = ctx.current;
        if (elem.type === 1 /* Open */ || elem.type === 2 /* Close */) {
            const tag = code.slice(elem.range[0], elem.range[1]);
            attributes(tag, elem.name).some(attr => {
                if (attr.name === 'style' && attr.value != null) {
                    const [valueStart, valueEnd] = attributeValueRange(tag, attr, elem.range[0]);
                    if (pos >= valueStart && pos <= valueEnd) {
                        cssCtx = getCSSContext(code.slice(valueStart, valueEnd), pos - valueStart, [valueStart, valueEnd]);
                        applyOffset(cssCtx, valueStart);
                        cssCtx.inline = true;
                        return true;
                    }
                }
            });
        }
    }
    else if (ctx.ancestors.length) {
        // Maybe inside `<style>` element?
        const parent = last(ctx.ancestors);
        if (parent.name === 'style') {
            // Find closing </style> tag
            const styleStart = parent.range[1];
            let styleEnd = code.length;
            scan(code.slice(parent.range[1]), (name, type, start) => {
                if (name === parent.name && type === 2 /* Close */) {
                    styleEnd = start + styleStart;
                    return false;
                }
            });
            cssCtx = getCSSContext(code.slice(styleStart, styleEnd), pos - styleStart, [styleStart, styleEnd]);
            applyOffset(cssCtx, styleStart);
        }
    }
    return cssCtx;
}
/**
 * Check if given tag is self-close for current parsing context
 */
function isSelfClose(name, options) {
    return !options.xml && options.empty.includes(name);
}
function allocItem(pool, name, type, start, end) {
    if (pool.length) {
        const tag = pool.pop();
        tag.name = name;
        tag.type = type;
        tag.start = start;
        tag.end = end;
        return tag;
    }
    return { name, type, start, end };
}
function releaseItem(pool, item) {
    if (item) {
        pool.push(item);
    }
}
function applyOffset(ctx, offset) {
    ctx.ancestors.forEach(item => {
        offsetRange(item.range, offset);
    });
    if (ctx.current) {
        offsetRange(ctx.current.range, offset);
    }
}
function offsetRange(range, offset) {
    range[0] += offset;
    range[1] += offset;
}

const JSX_PREFIX = '<';
const reJSXAbbrStart = /^[a-zA-Z.#\[\(]$/;
const reWordBound = /^[\s>;"\']?[a-zA-Z.#!@\[\(]$/;
const reStylesheetWordBound = /^[\s;"\']?[a-zA-Z!@]$/;
/**
 * Controller for tracking Emmet abbreviations in editor as user types.
 * Controller designed to be extended ad-hoc in editor plugins, overriding some
 * methods `mark()` to match editor behavior
 */
class AbbreviationTrackingController {
    constructor() {
        this.cache = new Map();
        this.trackers = new Map();
        this.lastPos = new Map();
    }
    /**
     * Returns last known location of caret in given editor
     */
    getLastPost(editor) {
        return this.lastPos.get(editor.id);
    }
    /**
     * Sets last known caret location for given editor
     */
    setLastPos(editor, pos) {
        this.lastPos.set(editor.id, pos);
    }
    /**
     * Returns abbreviation tracker for given editor, if any
     */
    getTracker(editor) {
        return this.trackers.get(editor.id);
    }
    /**
     * Detects if user is typing abbreviation at given location
     */
    typingAbbreviation(editor, pos) {
        var _a;
        // Start tracking only if user starts abbreviation typing: entered first
        // character at the word bound
        // NB: get last 2 characters: first should be a word bound(or empty),
        // second must be abbreviation start
        const prefix = editor.substr(Math.max(0, pos - 2), pos);
        const syntax = editor.syntax();
        let start = -1;
        let end = pos;
        let offset = 0;
        if (editor.isJSX(syntax)) {
            // In JSX, abbreviations should be prefixed
            if (prefix.length === 2 && prefix[0] === JSX_PREFIX && reJSXAbbrStart.test(prefix[1])) {
                start = pos - 2;
                offset = JSX_PREFIX.length;
            }
        }
        else if (reWordBound.test(prefix)) {
            start = pos - 1;
        }
        if (start >= 0) {
            // Check if there’s paired character
            const lastCh = prefix[prefix.length - 1];
            if (lastCh in pairs && editor.substr(pos, pos + 1) === pairs[lastCh]) {
                end++;
            }
            const config = this.getActivationContext(editor, pos);
            if (config) {
                if (config.type === 'stylesheet' && !reStylesheetWordBound.test(prefix)) {
                    // Additional check for stylesheet abbreviation start: it’s slightly
                    // differs from markup prefix, but we need activation context
                    // to ensure that context under caret is CSS
                    return;
                }
                const tracker = this.startTracking(editor, start, end, { offset, config });
                if (tracker && tracker.type === "abbreviation" /* Abbreviation */ && ((_a = config.context) === null || _a === void 0 ? void 0 : _a.name) === "@@section" /* Section */) {
                    // Make a silly check for section context: if user start typing
                    // CSS selector at the end of file, it will be treated as property
                    // name and provide unrelated completion by default.
                    // We should check if captured abbreviation actually matched
                    // snippet to continue. Otherwise, ignore this abbreviation.
                    // By default, unresolved abbreviations are converted to CSS properties,
                    // e.g. `a` → `a: ;`. If that’s the case, stop tracking
                    const { abbreviation, preview } = tracker;
                    if (preview.startsWith(abbreviation) && /^:\s*;?$/.test(preview.slice(abbreviation.length))) {
                        this.stopTracking(editor);
                        return;
                    }
                }
                return tracker;
            }
        }
    }
    /**
     * Starts abbreviation tracking for given editor
     * @param start Location of abbreviation start
     * @param pos Current caret position, must be greater that `start`
     */
    startTracking(editor, start, pos, params) {
        const config = (params === null || params === void 0 ? void 0 : params.config) || editor.config(start);
        const tracker = this.createTracker(editor, [start, pos], Object.assign({ config }, params));
        if (tracker) {
            this.trackers.set(editor.id, tracker);
            return tracker;
        }
        this.trackers.delete(editor.id);
    }
    /**
     * Stops abbreviation tracking in given editor instance
     */
    stopTracking(editor, params) {
        const tracker = this.getTracker(editor);
        if (tracker) {
            editor.unmark(tracker);
            if (tracker.forced && !(params === null || params === void 0 ? void 0 : params.skipRemove)) {
                // Contents of forced abbreviation must be removed
                editor.replace('', tracker.range[0], tracker.range[1]);
            }
            if (params === null || params === void 0 ? void 0 : params.force) {
                this.cache.delete(editor.id);
            }
            else {
                // Store tracker in history to restore it if user continues editing
                this.storeTracker(editor, tracker);
            }
            this.trackers.delete(editor.id);
        }
    }
    /**
     * Creates abbreviation tracker for given range in editor. Parses contents
     * of abbreviation in range and returns either valid abbreviation tracker,
     * error tracker or `null` if abbreviation cannot be created from given range
     */
    createTracker(editor, range, params) {
        if (range[0] >= range[1]) {
            // Invalid range
            return null;
        }
        let abbreviation = editor.substr(range[0], range[1]);
        const { config } = params;
        if (params.offset) {
            abbreviation = abbreviation.slice(params.offset);
        }
        // Basic validation: do not allow empty abbreviations
        // or newlines in abbreviations
        if (!abbreviation || /[\r\n]/.test(abbreviation)) {
            return null;
        }
        const base = {
            abbreviation,
            range,
            config,
            forced: !!params.forced,
            offset: params.offset || 0,
            lastPos: range[1],
            lastLength: editor.size(),
        };
        try {
            let parsedAbbr;
            let simple = false;
            if (config.type === 'stylesheet') {
                parsedAbbr = stylesheetAbbreviation(abbreviation);
            }
            else {
                parsedAbbr = markupAbbreviation(abbreviation, {
                    jsx: config.syntax === 'jsx'
                });
                simple = this.isSimpleMarkupAbbreviation(parsedAbbr);
            }
            const previewConfig = editor.previewConfig(config);
            return Object.assign(Object.assign({}, base), { type: "abbreviation" /* Abbreviation */, simple, preview: expand(parsedAbbr, previewConfig) });
        }
        catch (error) {
            return Object.assign(Object.assign({}, base), { type: "error" /* Error */, error });
        }
    }
    /**
     * Stores given tracker in separate cache to restore later
     */
    storeTracker(editor, tracker) {
        this.cache.set(editor.id, tracker);
    }
    /**
     * Returns stored tracker for given editor proxy, if any
     */
    getStoredTracker(editor) {
        return this.cache.get(editor.id);
    }
    /**
     * Tries to restore abbreviation tracker for given editor at specified position
     */
    restoreTracker(editor, pos) {
        const tracker = this.getStoredTracker(editor);
        if (tracker && tracker.range[0] <= pos && tracker.range[1] >= pos) {
            const [from, to] = tracker.range;
            if (editor.substr(from + tracker.offset, to) === tracker.abbreviation) {
                this.trackers.set(editor.id, tracker);
                tracker.lastLength = editor.size();
                return tracker;
            }
        }
    }
    /**
     * Handle content change in given editor instance
     */
    handleChange(editor, pos) {
        const tracker = this.getTracker(editor);
        const editorLastPos = this.getLastPost(editor);
        this.setLastPos(editor, pos);
        if (!tracker) {
            // No active tracker, check if we user is actually typing it
            if (editorLastPos != null && editorLastPos === pos - 1 && editor.allowTracking(pos)) {
                return this.typingAbbreviation(editor, pos);
            }
            return;
        }
        const { lastPos } = tracker;
        let { range } = tracker;
        if (lastPos < range[0] || lastPos > range[1]) {
            // Updated content outside abbreviation: reset tracker
            this.stopTracking(editor);
            return;
        }
        const length = editor.size();
        const delta = length - tracker.lastLength;
        range = range.slice();
        // Modify range and validate it: if it leads to invalid abbreviation, reset tracker
        updateRange(range, delta, lastPos);
        // Handle edge case: empty forced abbreviation is allowed
        if (range[0] === range[1] && tracker.forced) {
            tracker.abbreviation = '';
            return tracker;
        }
        const nextTracker = this.createTracker(editor, range, tracker);
        if (!nextTracker || (!tracker.forced && !isValidTracker(nextTracker, range, pos))) {
            this.stopTracking(editor);
            return;
        }
        nextTracker.lastPos = pos;
        this.trackers.set(editor.id, nextTracker);
        editor.mark(nextTracker);
        return nextTracker;
    }
    /**
     * Handle selection (caret) change in given editor instance
     */
    handleSelectionChange(editor, pos) {
        this.setLastPos(editor, pos);
        const tracker = this.getTracker(editor) || this.restoreTracker(editor, pos);
        if (tracker) {
            tracker.lastPos = pos;
            return tracker;
        }
    }
    /**
     * Detects and returns valid abbreviation activation context for given location
     * in editor which can be used for abbreviation expanding.
     * For example, in given HTML code:
     * `<div title="Sample" style="">Hello world</div>`
     * it’s not allowed to expand abbreviations inside `<div ...>` or `</div>`,
     * yet it’s allowed inside `style` attribute and between tags.
     *
     * This method ensures that given `pos` is inside location allowed for expanding
     * abbreviations and returns context data about it.
     *
     * Default implementation works for any editor since it uses own parsers for HTML
     * and CSS but might be slow: if your editor supports low-level access to document
     * parse tree or tokens, authors should override this method and provide alternative
     * based on editor native features.
     */
    getActivationContext(editor, pos) {
        const syntax = editor.syntax();
        const content = editor.substr();
        if (editor.isCSS(syntax)) {
            return this.getCSSActivationContext(editor, pos, syntax, getCSSContext(content, pos));
        }
        if (editor.isHTML(syntax)) {
            const ctx = getHTMLContext(content, pos, { xml: editor.isXML(syntax) });
            if (ctx.css) {
                return this.getCSSActivationContext(editor, pos, getEmbeddedStyleSyntax(content, ctx) || 'css', ctx.css);
            }
            if (!ctx.current) {
                return {
                    syntax,
                    type: 'markup',
                    context: getMarkupAbbreviationContext(content, ctx),
                    options: editor.outputOptions(pos)
                };
            }
        }
        else {
            return {
                syntax,
                type: editor.syntaxType(syntax),
                options: editor.outputOptions(pos)
            };
        }
    }
    getCSSActivationContext(editor, pos, syntax, ctx) {
        // CSS abbreviations can be activated only when a character is entered, e.g.
        // it should be either property name or value.
        // In come cases, a first character of selector should also be considered
        // as activation context
        if (!ctx.current) {
            return void 0;
        }
        const allowedContext = ctx.current.type === "propertyName" /* PropertyName */
            || ctx.current.type === "propertyValue" /* PropertyValue */
            || this.isTypingBeforeSelector(editor, pos, ctx);
        if (allowedContext) {
            return {
                syntax,
                type: 'stylesheet',
                context: getStylesheetAbbreviationContext(ctx),
                options: editor.outputOptions(pos, ctx.inline)
            };
        }
    }
    /**
     * Handle edge case: start typing abbreviation before selector. In this case,
     * entered character becomes part of selector
     * Activate only if it’s a nested section and it’s a first character of selector
     */
    isTypingBeforeSelector(editor, pos, { current }) {
        if (current && current.type === "selector" /* Selector */ && current.range[0] === pos - 1) {
            // Typing abbreviation before selector is tricky one:
            // ensure it’s on its own line
            const line = editor.substr(current.range[0], current.range[1]).split(/[\n\r]/)[0];
            return line.trim().length === 1;
        }
        return false;
    }
    /**
     * Check if given parsed markup abbreviation is simple.A simple abbreviation
     * may not be displayed to user as preview to reduce distraction
     */
    isSimpleMarkupAbbreviation(abbr) {
        if (abbr.children.length === 1 && !abbr.children[0].children.length) {
            // Single element: might be a HTML element or text snippet
            const first = abbr.children[0];
            // XXX silly check for common snippets like `!`. Should read contents
            // of expanded abbreviation instead
            return !first.name || /^[a-z]/i.test(first.name);
        }
        return !abbr.children.length;
    }
    /**
     * Method should be called when given editor instance will be no longer
     * available to clean up cached data
     */
    disposeEditor(editor) {
        this.cache.delete(editor.id);
        this.trackers.delete(editor.id);
        this.lastPos.delete(editor.id);
    }
}
function updateRange(range, delta, lastPos) {
    if (delta < 0) {
        // Content removed
        if (lastPos === range[0]) {
            // Updated content at the abbreviation edge
            range[0] += delta;
            range[1] += delta;
        }
        else if (range[0] < lastPos && lastPos <= range[1]) {
            range[1] += delta;
        }
    }
    else if (delta > 0 && range[0] <= lastPos && lastPos <= range[1]) {
        // Content inserted
        range[1] += delta;
    }
    return range;
}
/**
 * Check if given tracker is in valid state for keeping it marked
 */
function isValidTracker(tracker, range, pos) {
    if (tracker.type === "error" /* Error */) {
        if (range[1] === pos) {
            // Last entered character is invalid
            return false;
        }
        const { abbreviation } = tracker;
        const start = range[0];
        let targetPos = range[1];
        while (targetPos > start) {
            if (pairsEnd.includes(abbreviation[targetPos - start - 1])) {
                targetPos--;
            }
            else {
                break;
            }
        }
        return targetPos !== pos;
    }
    return true;
}

export { AbbreviationTrackingController, JSX_PREFIX, findTagMatch, getCSSContext, getCSSSection, getHTMLContext, getOpenTag, getTagMatches, selectItemCSS, selectItemHTML };
//# sourceMappingURL=action-utils.es.js.map
